import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaCoins,
  FaRegCalendarCheck,
} from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import PageLoader from "../../components/PageLoader";
import { supabase } from "../../supabaseClient";
import { formatProfileName } from "../../utils/profileName";

const DEFAULT_ROBUX_PER_MINUTE = 20;
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function toDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMonday(date = new Date()) {
  const result = new Date(date);
  const day = result.getDay();
  result.setDate(result.getDate() + (day === 0 ? -6 : 1 - day));
  result.setHours(0, 0, 0, 0);
  return result;
}

function addDays(date, amount) {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
}

function isMonday(value) {
  return parseLocalDate(value)?.getDay() === 1;
}

function formatDate(value) {
  const date = parseLocalDate(value);
  if (!date) return "Date unavailable";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function calculateMinutes(start, end, breakMinutes) {
  if (!start || !end) return 0;

  const [startHour, startMinute] = start.slice(0, 5).split(":").map(Number);
  const [endHour, endMinute] = end.slice(0, 5).split(":").map(Number);

  let startTotal = startHour * 60 + startMinute;
  let endTotal = endHour * 60 + endMinute;
  if (endTotal < startTotal) endTotal += 24 * 60;

  return Math.max(endTotal - startTotal - Number(breakMinutes || 0), 0);
}

function statusClass(status) {
  return `pguh-timesheet-status--${String(status || "Submitted").toLowerCase()}`;
}

function settingToBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value && typeof value === "object" && "enabled" in value) {
    return Boolean(value.enabled);
  }
  return value !== false;
}

function timesheetErrorMessage(error) {
  if (!error) return "Your timesheet could not be loaded.";

  if (error.code === "42703" || /week_start|shift_date|start_time|end_time/i.test(error.message || "")) {
    return "The timesheet database needs the latest PGUH upgrade. Run FIX_TIMESHEET_AND_PAGER.sql in Supabase, then refresh this page.";
  }

  if (error.code === "42501" || error.code === "PGRST301") {
    return "Your account does not currently have permission to read this timesheet.";
  }

  return error.message || "Your timesheet could not be loaded.";
}

export default function Timesheet({ session, profile }) {
  const userId = session?.user?.id || profile?.id || null;
  const profileName = formatProfileName(profile);
  const profilePosition = profile?.position || "Staff";
  const robuxPerMinute = Number(
    profile?.robux_pay_rate || DEFAULT_ROBUX_PER_MINUTE,
  );

  const [weekStart, setWeekStart] = useState(toDateInput(getMonday()));
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetsEnabled, setTimesheetsEnabled] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    id: null,
    shift_date: "",
    start_time: "",
    end_time: "",
    break_minutes: 0,
    notes: "",
    status: "Submitted",
  });

  const weekDays = useMemo(() => {
    const monday = parseLocalDate(weekStart);
    return DAYS.map((day, index) => {
      const date = addDays(monday, index);
      return { day, dateInput: toDateInput(date) };
    });
  }, [weekStart]);

  const weekLabel = useMemo(() => {
    const monday = parseLocalDate(weekStart);
    if (!monday) return "Selected week";

    const sunday = addDays(monday, 6);
    const startLabel = monday.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
    const endLabel = sunday.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    return `${startLabel} – ${endLabel}`;
  }, [weekStart]);

  const weeklyTotals = useMemo(() => {
    return timesheets.reduce(
      (totals, entry) => ({
        minutes: totals.minutes + Number(entry.total_minutes || 0),
        robux: totals.robux + Number(entry.robux_pay || 0),
      }),
      { minutes: 0, robux: 0 },
    );
  }, [timesheets]);

  const previewMinutes = useMemo(
    () =>
      calculateMinutes(
        form.start_time,
        form.end_time,
        form.break_minutes,
      ),
    [form.break_minutes, form.end_time, form.start_time],
  );
  const previewRobux = previewMinutes * robuxPerMinute;

  const draftKey = useMemo(
    () =>
      userId && selectedDay
        ? `pguh:timesheet-draft:${userId}:${weekStart}:${selectedDay.dateInput}`
        : null,
    [selectedDay, userId, weekStart],
  );

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setMessage(null);

      const [settingResult, timesheetResult] = await Promise.all([
        supabase
          .from("app_settings")
          .select("value")
          .eq("key", "timesheets_enabled")
          .maybeSingle(),
        supabase
          .from("timesheets")
          .select(
            "id, user_id, staff_name, staff_position, week_start, shift_date, start_time, end_time, break_minutes, notes, total_minutes, robux_per_minute, robux_pay, status, created_at, updated_at",
          )
          .eq("user_id", userId)
          .eq("week_start", weekStart)
          .order("shift_date", { ascending: true }),
      ]);

      if (cancelled) return;

      if (!settingResult.error && settingResult.data) {
        setTimesheetsEnabled(settingToBoolean(settingResult.data.value));
      }

      if (timesheetResult.error) {
        console.error("Unable to load timesheets:", timesheetResult.error);
        setMessage({
          type: "error",
          text: timesheetErrorMessage(timesheetResult.error),
        });
        setTimesheets([]);
      } else {
        setTimesheets(timesheetResult.data || []);
      }

      setSelectedDay(null);
      setForm({
        id: null,
        shift_date: "",
        start_time: "",
        end_time: "",
        break_minutes: 0,
        notes: "",
        status: "Submitted",
      });
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [userId, weekStart]);

  useEffect(() => {
    if (!draftKey || !selectedDay) return;

    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          start_time: form.start_time,
          end_time: form.end_time,
          break_minutes: form.break_minutes,
          notes: form.notes,
        }),
      );
    } catch (error) {
      console.warn("Unable to save the timesheet draft:", error);
    }
  }, [draftKey, form.break_minutes, form.end_time, form.notes, form.start_time, selectedDay]);

  const entryForDate = (dateInput) =>
    timesheets.find((entry) => entry.shift_date === dateInput);

  const selectDay = (day) => {
    const existing = entryForDate(day.dateInput);
    let draft = null;
    const nextDraftKey = userId
      ? `pguh:timesheet-draft:${userId}:${weekStart}:${day.dateInput}`
      : null;

    if (!existing && nextDraftKey) {
      try {
        draft = JSON.parse(localStorage.getItem(nextDraftKey) || "null");
      } catch {
        draft = null;
      }
    }

    setSelectedDay(day);
    setMessage(null);
    setForm({
      id: existing?.id || null,
      shift_date: day.dateInput,
      start_time: existing?.start_time?.slice(0, 5) || draft?.start_time || "",
      end_time: existing?.end_time?.slice(0, 5) || draft?.end_time || "",
      break_minutes: existing?.break_minutes ?? draft?.break_minutes ?? 0,
      notes: existing?.notes || draft?.notes || "",
      status: existing?.status || "Submitted",
    });
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const saveEntry = async (event) => {
    event.preventDefault();

    if (!timesheetsEnabled) {
      setMessage({
        type: "error",
        text: "Timesheet logging is currently disabled by Management.",
      });
      return;
    }

    if (!selectedDay || !form.start_time || !form.end_time) {
      setMessage({
        type: "error",
        text: "Select a day and enter both a start and end time.",
      });
      return;
    }

    if (previewMinutes <= 0) {
      setMessage({
        type: "error",
        text: "The worked time must be more than 0 minutes.",
      });
      return;
    }

    if (["Approved", "Paid"].includes(form.status)) {
      setMessage({
        type: "error",
        text: "Approved or paid entries can only be changed by Management.",
      });
      return;
    }

    setSaving(true);
    setMessage(null);

    const payload = {
      user_id: userId,
      staff_name: profileName,
      staff_position: profilePosition,
      week_start: weekStart,
      shift_date: form.shift_date,
      start_time: form.start_time,
      end_time: form.end_time,
      break_minutes: Number(form.break_minutes || 0),
      notes: form.notes.trim(),
      total_minutes: previewMinutes,
      robux_pay: previewRobux,
      robux_per_minute: robuxPerMinute,
      status: form.status === "Rejected" ? "Submitted" : form.status,
    };

    const result = form.id
      ? await supabase.from("timesheets").update(payload).eq("id", form.id)
      : await supabase.from("timesheets").insert(payload).select("*").single();

    setSaving(false);

    if (result.error) {
      console.error("Unable to save timesheet:", result.error);
      setMessage({
        type: "error",
        text: "The timesheet entry could not be saved.",
      });
      return;
    }

    const { data } = await supabase
      .from("timesheets")
      .select(
        "id, user_id, staff_name, staff_position, week_start, shift_date, start_time, end_time, break_minutes, notes, total_minutes, robux_per_minute, robux_pay, status, created_at, updated_at",
      )
      .eq("user_id", userId)
      .eq("week_start", weekStart)
      .order("shift_date", { ascending: true });

    setTimesheets(data || []);
    setForm((current) => ({
      ...current,
      id: result.data?.id || current.id,
      status: "Submitted",
    }));

    if (draftKey) localStorage.removeItem(draftKey);

    setMessage({
      type: "success",
      text: "Timesheet entry saved successfully.",
    });
  };

  const clearEntry = async () => {
    if (!form.id) {
      setForm((current) => ({
        ...current,
        start_time: "",
        end_time: "",
        break_minutes: 0,
        notes: "",
      }));
      if (draftKey) localStorage.removeItem(draftKey);
      return;
    }

    if (["Approved", "Paid"].includes(form.status)) {
      setMessage({
        type: "error",
        text: "Approved or paid entries can only be removed by Management.",
      });
      return;
    }

    if (!window.confirm("Remove this timesheet entry?")) return;

    const { error } = await supabase
      .from("timesheets")
      .delete()
      .eq("id", form.id);

    if (error) {
      setMessage({ type: "error", text: "The entry could not be removed." });
      return;
    }

    setTimesheets((current) => current.filter((entry) => entry.id !== form.id));
    setForm({
      id: null,
      shift_date: selectedDay?.dateInput || "",
      start_time: "",
      end_time: "",
      break_minutes: 0,
      notes: "",
      status: "Submitted",
    });
    if (draftKey) localStorage.removeItem(draftKey);
    setMessage({ type: "success", text: "Timesheet entry removed." });
  };

  const changeWeek = (direction) => {
    const current = parseLocalDate(weekStart);
    current.setDate(current.getDate() + direction * 7);
    setWeekStart(toDateInput(current));
  };

  const handleWeekInput = (value) => {
    if (!isMonday(value)) {
      setMessage({
        type: "error",
        text: "Timesheet weeks must start on a Monday.",
      });
      return;
    }
    setWeekStart(value);
  };

  if (loading) return <PageLoader label="Loading your timesheet" />;

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />

      <main id="main-content" tabIndex="-1" className="pguh-main">
        <div className="nhsuk-width-container">
          <section className="pguh-timesheet-hero">
            <div>
              <span className="pguh-eyebrow">Staff payroll</span>
              <h1 className="nhsuk-heading-xl">Weekly timesheet</h1>
              <p className="nhsuk-body-l">
                Record worked time for each day. Pay is calculated at{" "}
                <strong>{robuxPerMinute} Robux per minute</strong>.
              </p>
            </div>
            <div className="pguh-timesheet-identity">
              <span>Signed in as</span>
              <strong>{profileName}</strong>
              <small>{profilePosition}</small>
              <small>{robuxPerMinute} Robux/min</small>
            </div>
          </section>

          {!timesheetsEnabled && (
            <div className="nhsuk-warning-callout" role="alert">
              <h2 className="nhsuk-warning-callout__label">
                Timesheet logging is disabled
              </h2>
              <p>
                Management has temporarily disabled new timesheet submissions.
                Existing records remain visible.
              </p>
            </div>
          )}

          {message && (
            <div
              className={`pguh-timesheet-message pguh-timesheet-message--${message.type}`}
              role={message.type === "error" ? "alert" : "status"}
            >
              {message.text}
            </div>
          )}

          <section className="pguh-timesheet-toolbar" aria-label="Choose timesheet week">
            <button
              type="button"
              className="pguh-timesheet-toolbar__nav"
              onClick={() => changeWeek(-1)}
            >
              <FaArrowLeft aria-hidden="true" />
              <span>Previous week</span>
            </button>

            <div className="pguh-timesheet-toolbar__date">
              <span className="pguh-timesheet-toolbar__icon" aria-hidden="true">
                <FaCalendarAlt />
              </span>
              <div>
                <label htmlFor="timesheet-week">Week starting</label>
                <input
                  id="timesheet-week"
                  type="date"
                  value={weekStart}
                  onChange={(event) => handleWeekInput(event.target.value)}
                />
                <small>{weekLabel} · Choose a Monday.</small>
              </div>
            </div>

            <button
              type="button"
              className="pguh-timesheet-toolbar__nav pguh-timesheet-toolbar__nav--next"
              onClick={() => changeWeek(1)}
            >
              <span>Next week</span>
              <FaArrowRight aria-hidden="true" />
            </button>
          </section>

          <section className="pguh-timesheet-summary" aria-label="Week totals">
            <div>
              <span className="pguh-timesheet-summary__icon"><FaClock aria-hidden="true" /></span>
              <span>Total minutes</span>
              <strong>{weeklyTotals.minutes.toLocaleString("en-GB")}</strong>
            </div>
            <div>
              <span className="pguh-timesheet-summary__icon"><FaCoins aria-hidden="true" /></span>
              <span>Total Robux</span>
              <strong>{weeklyTotals.robux.toLocaleString("en-GB")}</strong>
            </div>
            <div>
              <span className="pguh-timesheet-summary__icon"><FaRegCalendarCheck aria-hidden="true" /></span>
              <span>Recorded days</span>
              <strong>{timesheets.length}/7</strong>
            </div>
            <div>
              <span className="pguh-timesheet-summary__icon"><FaCoins aria-hidden="true" /></span>
              <span>Pay rate</span>
              <strong>{robuxPerMinute}/min</strong>
            </div>
          </section>

          <div className="pguh-timesheet-layout">
            <section className="pguh-timesheet-calendar" aria-labelledby="timesheet-calendar-heading">
              <div className="pguh-panel__header">
                <div>
                  <span className="pguh-eyebrow">Week view</span>
                  <h2 id="timesheet-calendar-heading" className="nhsuk-heading-l">
                    Recorded time
                  </h2>
                </div>
              </div>

              <div className="pguh-timesheet-days">
                {weekDays.map((day) => {
                  const entry = entryForDate(day.dateInput);
                  const selected = selectedDay?.dateInput === day.dateInput;
                  const status = entry?.status || "Not set";
                  const isToday = day.dateInput === toDateInput(new Date());

                  return (
                    <article
                      className={`pguh-timesheet-day${selected ? " pguh-timesheet-day--selected" : ""}${isToday ? " pguh-timesheet-day--today" : ""}`}
                      key={day.dateInput}
                    >
                      <div className="pguh-timesheet-day__heading">
                        <div>
                          <h3>
                            {day.day}
                            {isToday && <small className="pguh-timesheet-day__today">Today</small>}
                          </h3>
                          <span>{formatDate(day.dateInput)}</span>
                        </div>
                        <span className={`pguh-timesheet-status ${entry ? statusClass(status) : "pguh-timesheet-status--empty"}`}>
                          {status}
                        </span>
                      </div>

                      {entry ? (
                        <dl>
                          <div><dt>Time</dt><dd>{entry.start_time?.slice(0, 5)}–{entry.end_time?.slice(0, 5)}</dd></div>
                          <div><dt>Break</dt><dd>{entry.break_minutes || 0} mins</dd></div>
                          <div><dt>Pay</dt><dd>{Number(entry.robux_pay || 0).toLocaleString("en-GB")} Robux</dd></div>
                        </dl>
                      ) : (
                        <p>No time recorded.</p>
                      )}

                      <button type="button" onClick={() => selectDay(day)}>
                        {entry ? "Review or update" : "Add time"}
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>

            <aside className="pguh-timesheet-editor" aria-labelledby="timesheet-editor-heading">
              <div className="pguh-panel__header">
                <div>
                  <span className="pguh-eyebrow">Entry editor</span>
                  <h2 id="timesheet-editor-heading" className="nhsuk-heading-l">
                    {selectedDay ? selectedDay.day : "Select a day"}
                  </h2>
                  {selectedDay && <p>{formatDate(selectedDay.dateInput)}</p>}
                </div>
              </div>

              {!selectedDay ? (
                <div className="pguh-empty-state">
                  <h3>No day selected</h3>
                  <p>Choose a day from the week to enter or review worked time.</p>
                </div>
              ) : (
                <form onSubmit={saveEntry}>
                  {["Approved", "Paid"].includes(form.status) && (
                    <p className="pguh-timesheet-locked">
                      This entry is {form.status.toLowerCase()} and is read-only.
                    </p>
                  )}

                  <div className="pguh-timesheet-time-grid">
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="timesheet-start">Start time</label>
                      <input
                        id="timesheet-start"
                        className="nhsuk-input"
                        type="time"
                        value={form.start_time}
                        onChange={(event) => updateForm("start_time", event.target.value)}
                        disabled={!timesheetsEnabled || ["Approved", "Paid"].includes(form.status)}
                      />
                    </div>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="timesheet-end">End time</label>
                      <input
                        id="timesheet-end"
                        className="nhsuk-input"
                        type="time"
                        value={form.end_time}
                        onChange={(event) => updateForm("end_time", event.target.value)}
                        disabled={!timesheetsEnabled || ["Approved", "Paid"].includes(form.status)}
                      />
                    </div>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="timesheet-break">Break minutes</label>
                    <input
                      id="timesheet-break"
                      className="nhsuk-input nhsuk-input--width-5"
                      type="number"
                      min="0"
                      max="1440"
                      value={form.break_minutes}
                      onChange={(event) => updateForm("break_minutes", event.target.value)}
                      disabled={!timesheetsEnabled || ["Approved", "Paid"].includes(form.status)}
                    />
                  </div>

                  <div className="pguh-timesheet-calculation">
                    <div><span>Worked time</span><strong>{previewMinutes} minutes</strong></div>
                    <div><span>Estimated pay</span><strong>{previewRobux.toLocaleString("en-GB")} Robux</strong></div>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="timesheet-notes">Work notes</label>
                    <textarea
                      id="timesheet-notes"
                      className="nhsuk-textarea"
                      rows="5"
                      maxLength="1500"
                      value={form.notes}
                      onChange={(event) => updateForm("notes", event.target.value)}
                      placeholder="Briefly describe the work completed"
                      disabled={!timesheetsEnabled || ["Approved", "Paid"].includes(form.status)}
                    />
                  </div>

                  <div className="pguh-timesheet-form-actions">
                    <button
                      className="nhsuk-button"
                      type="submit"
                      disabled={saving || !timesheetsEnabled || ["Approved", "Paid"].includes(form.status)}
                    >
                      {saving ? "Saving…" : form.id ? "Update entry" : "Save entry"}
                    </button>
                    <button
                      className="nhsuk-button nhsuk-button--secondary"
                      type="button"
                      onClick={clearEntry}
                      disabled={saving || ["Approved", "Paid"].includes(form.status)}
                    >
                      {form.id ? "Remove entry" : "Clear form"}
                    </button>
                  </div>
                </form>
              )}
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
